'use client';

import {
  bankTransferProgress,
  formatFiatUnits,
  type OnrampClient,
  unitsToDecimal,
} from '@morfi/onramp-sdk';
import { useBankTransferInstructions } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useFormattedCurrency } from '@/lib/currency/use-formatted-currency';
import { logger } from '@/lib/telemetry/logger';
import { FundingLoading } from '../shared/funding-loading';
import { useFundingPageActive } from '../shared/use-funding-page-active';

export function BankTransferInstructions({
  client,
  operationId,
  onDismiss,
  onViewStatus = onDismiss,
}: {
  client: OnrampClient;
  operationId: string;
  onDismiss(): void;
  onViewStatus?(): void;
}): ReactNode {
  const t = useTranslations('onramp');
  const { formatD6 } = useFormattedCurrency();
  const fieldLabels = {
    amount: t('bank_fields.amount'),
    reference: t('bank_fields.reference'),
    recipientName: t('bank_fields.recipientName'),
    recipientAddress: t('bank_fields.recipientAddress'),
    iban: t('bank_fields.iban'),
    bic: t('bank_fields.bic'),
    bankName: t('bank_fields.bankName'),
    bankAddress: t('bank_fields.bankAddress'),
  };
  const locale = useLocale();
  const details = useBankTransferInstructions(client, operationId, useFundingPageActive());
  const [copyFailed, setCopyFailed] = useState(false);
  useEffect(() => {
    if (details.failed)
      logger.warn('onramp.bank_instructions_failed', { reason: 'instructions_unavailable' });
  }, [details.failed]);
  if (details.failed)
    return (
      <div className="flex flex-col gap-4">
        <p role="alert">{t('bank_instructions_unavailable')}</p>
        <AnimatedButton onClick={details.retry}>{t('retry')}</AnimatedButton>
        <AnimatedButton variant="tertiary" onClick={onViewStatus}>
          {t('bank_view_status')}
        </AnimatedButton>
      </div>
    );
  if (!details.data) return <FundingLoading />;
  const data = details.data;
  if (bankTransferProgress(data.status, data.stages) !== 'awaiting_transfer')
    return <AnimatedButton onClick={onViewStatus}>{t('bank_view_status')}</AnimatedButton>;
  const amount = unitsToDecimal(BigInt(data.amountUnits), 2);
  const fields = [
    ['amount', amount],
    ['reference', data.reference],
    ['recipientName', data.recipientName],
    ['recipientAddress', data.recipientAddress],
    ['iban', data.iban],
    ['bic', data.bic],
    ['bankName', data.bankName],
    ['bankAddress', data.bankAddress],
  ] as const;
  async function copy(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopyFailed(false);
    } catch {
      setCopyFailed(true);
      logger.warn('onramp.bank_instruction_copy_failed', { reason: 'clipboard_unavailable' });
    }
  }
  return (
    <div className="rr-block flex flex-col gap-4">
      <div className="my-3.5 border-l-2 border-border-brand-strong bg-background-brand px-4 py-3 text-caption text-foreground-secondary">
        <h3 className="font-medium">{t('bank_exact_amount')}</h3>
        <p className="mt-1 text-foreground-secondary">{t('bank_reference_warning')}</p>
      </div>
      {fields.map(([field, value]) =>
        value ? (
          <div
            key={field}
            className="flex items-center justify-between gap-3 rounded-control bg-background-secondary px-3 py-2.5 text-caption"
          >
            <div className="min-w-0">
              <p className="text-caption text-foreground-secondary">{fieldLabels[field]}</p>
              <p className="break-words text-caption font-medium">
                {field === 'amount' ? formatFiatUnits(data.amountUnits, 2, 'EUR', locale) : value}
              </p>
            </div>
            <AnimatedButton
              variant="tertiary"
              onClick={(): void => {
                void copy(value);
              }}
            >
              {t('bank_copy')}
            </AnimatedButton>
          </div>
        ) : null,
      )}
      {copyFailed ? (
        <p role="alert" className="text-caption">
          {t('bank_copy_failed')}
        </p>
      ) : null}
      <div className="flex justify-between text-caption">
        <span>{t('bank_estimated_receipt')}</span>
        <span>~{formatD6(data.estimatedReceivedD6, 'full')}</span>
      </div>
      <p className="text-caption text-foreground-secondary">{t('bank_arrival_estimate')}</p>
      <AnimatedButton onClick={onDismiss}>{t('bank_transfer_sent')}</AnimatedButton>
      <p className="text-caption text-foreground-secondary">{t('bank_transfer_sent_disclosure')}</p>
    </div>
  );
}
